/**
 * 国际化（i18n）：文案字典 + 当前语言存取。
 * - 默认英文（'en'）；标题画面按 C 在 en ↔ zh 间切换，localStorage('kimi-lang') 持久化。
 * - t(id) 按当前语言取文案：当前语言缺失回落 en，再缺失回落 id 本身。
 * - tf(id, params) 在 t() 基础上做 {key} 占位替换（带数字的文案用）。
 * - charName/diffName：角色/难度显示名按语言二选一（英 romaji/name，中 cnName）。
 * - 本模块不直接触浏览器 API（localStorage 经 globalThis + try/catch 兜底，无痕模式安全），
 *   node 可直接 import。
 * - 文件布局：本文件为逻辑；数据表在同目录 strings.js（STRINGS）与
 *   content-en/ja/ko.js（CONTENT 三表）；../i18n.js 是兼容旧路径的 barrel。
 */

import { STRINGS } from './strings.js';
import { CONTENT_EN } from './content-en.js';
import { CONTENT_JA } from './content-ja.js';
import { CONTENT_KO } from './content-ko.js';

const STORAGE_KEY = 'kimi-lang';

/** 支持的语言与显示名（标题画面按 C 循环切换，localStorage 持久化）。 */
export const LANGS = ['en', 'zh', 'ja', 'ko'];
export const LANG_NAMES = { en: 'EN', zh: '中文', ja: '日本語', ko: '한국어' };

/** 当前语言（LANGS 之一）；null = 尚未从存储读取（惰性加载，保持模块顶层纯净）。 */
let _lang = null;

function _load() {
  if (_lang !== null) return;
  _lang = 'en';
  try {
    const v = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (LANGS.includes(v)) _lang = v;
  } catch (e) { /* 无痕模式等：保持默认 en */ }
}

/** 当前语言。 */
export function getLang() {
  _load();
  return _lang;
}

/** 设置语言并持久化（仅接受 LANGS 成员）。 */
export function setLang(l) {
  if (!LANGS.includes(l)) return;
  _lang = l;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, l);
  } catch (e) { /* 存储失败忽略 */ }
}

/** 按 LANGS 顺序循环切换，返回切换后的语言。 */
export function toggleLang() {
  setLang(LANGS[(LANGS.indexOf(getLang()) + 1) % LANGS.length]);
  return getLang();
}

/** 取文案：当前语言 → en → id 本身。 */
export function t(id) {
  _load();
  const e = STRINGS[id];
  if (!e) return id;
  return e[_lang] ?? e.en ?? id;
}

/** 带 {key} 占位替换的 t()（数字插值文案用）。 */
export function tf(id, params) {
  let s = t(id);
  for (const k in params) s = s.split(`{${k}}`).join(String(params[k]));
  return s;
}

/** 角色显示名：中文用 cnName，其他语言用 romaji（name）。 */
export function charName(c) {
  if (!c) return '?';
  return getLang() === 'zh' ? c.cnName : c.name;
}

/** 难度显示名：中文用 cnName；ja/ko 有专名用专名（如 EXTRA→エクストラ/추가），否则用 name。 */
export function diffName(d) {
  if (!d) return '?';
  const lang = getLang();
  if (lang === 'zh') return d.cnName;
  if (lang === 'ja' && d.jaName) return d.jaName;
  if (lang === 'ko' && d.koName) return d.koName;
  return d.name;
}

/**
 * 内容文案本地化：en 查 CONTENT_EN，ja 查 CONTENT_JA，ko 查 CONTENT_KO
 *（无表项回落原文），zh 恒返回原文。
 * 用于角色/关卡/boss/符卡/台词/曲目等内容数据的展示处包装。
 */
export function L(s) {
  if (s == null) return s;
  const lang = getLang();
  if (lang === 'zh') return s;
  if (lang === 'ja') return CONTENT_JA[s] ?? s;
  if (lang === 'ko') return CONTENT_KO[s] ?? s;
  return CONTENT_EN[s] ?? s;
}
